import { inject, Injectable, Injector } from "@angular/core";
import { Log, User, UserManager, UserManagerSettings } from "oidc-client-ts";
import { Subject } from "rxjs";

import { LgConsole } from "@logex/framework/core";
import { LgPromptDialog } from "@logex/framework/ui-core";
import { LgNetworkErrorService } from "../error-tracking/lg-network-error.service";
import { ITokenAuthenticationService, UserIdentity } from "./authentication.types";

export interface OidcAuthServiceConfig {
    authority: string;
    clientId: string;
    audience: string;
    type: "auth0" | "generic";
    basePath: string; // If hosted in root then basePath should be "/"
    redirectUrl: string;
    scope?: string | undefined;
}

const DELAYS_IN_SECONDS = [5, 7, 11, 17, 23, 31, 43, 59];
const SESSION_STORAGE_KEY_NAME = "lgLoginStage";
const SESSION_STORAGE_AUTHORITY_NAME = "lgAuthority";
const SESSION_STORAGE_CLIENT_ID_NAME = "lgClientId";
const SILENT_RENEW_RETRY_DELAY_MS = 5000;

@Injectable()
export class OidcAuthService implements ITokenAuthenticationService {
    private _injector = inject(Injector);
    private _lgConsole = inject(LgConsole).withSource("Logex.OidcAuthService");

    constructor() {
        this._loginPromise = new Promise<void>(resolve => {
            this._loginPromiseResolve = resolve;
        });
    }

    // ----------------------------------------------------------------------------------

    private _config!: OidcAuthServiceConfig;
    private _userManager?: UserManager;
    private _user!: User;
    private _userIdentity?: UserIdentity;
    private _loginPromise!: Promise<void>;
    private _loginPromiseResolve!: () => void;

    // ----------------------------------------------------------------------------------
    configure(config: OidcAuthServiceConfig): void {
        this._config = config;
    }

    async login(): Promise<boolean> {
        if (this.loggedIn) {
            throw Error("User can be only logged in once");
        }

        const authority = this._getNormalizedAuthority();

        this._setAuthorityInSessionStorage(authority);

        const userManager = this._getUserManager(authority);
        let user: User | null = null;

        const loginStage = this._getLoginStage();
        if (loginStage === 0) {
            // If it's a fist stage of login process, then we first try to get a new auth_token from auth provider
            try {
                user = await userManager.signinSilent();
            } catch (e: any) {
                if (
                    e.error === "login_required" ||
                    e.error === "consent_required" ||
                    e.error === "access_denied" ||
                    e.error === "interaction_required" ||
                    e.message === "Frame window timed out" ||
                    e.message === "IFrame timed out without a response"
                ) {
                    // If silent signin requested login, then record that in the state, so the next time we get
                    // the token from the session storage
                    this._setLoginStage(1);

                    // This call causes page redirection, so we stop the execution until the application will be restarted
                    await userManager.signinRedirect({ state: this._config.redirectUrl });
                    return false;
                }

                // Track network errors during initial silent signin (e.g., "Frame window timed out")
                // Don't throw - the auto-reload + error dialog flow will handle recovery.
                const errorGuid = this._trackNetworkError(e);
                this._lgConsole.error(
                    `OIDC silent signin failed. Error GUID: ${errorGuid ?? "N/A"}`,
                    e
                );

                return false;
            }
        } else if (loginStage > 0) {
            // If we are coming back from `signin-callback.html` then we need to take the user from session storage
            user = await userManager.getUser();
            if (user == null) {
                this._setLoginStage(0);
                await userManager.signinRedirect({ state: this._config.redirectUrl });
                return false;
            }
        }

        // User is finally logged in, so we can erase the login stage indicator
        this._setLoginStage(null);

        // Subscribe to event after user is successfully logged in
        this._userManager!.events.addSilentRenewError(this._onSilentRenewError.bind(this));
        this._userManager!.events.addAccessTokenExpired(this._onAccessTokenExpired.bind(this));

        this._user = user!;
        this._loginPromiseResolve();

        return true;
    }

    private _getLoginStage(): number {
        const value = window.sessionStorage.getItem(SESSION_STORAGE_KEY_NAME) ?? "";
        return parseInt(value) || 0;
    }

    private _setLoginStage(stage: number | null): void {
        if (stage != null) {
            window.sessionStorage.setItem(SESSION_STORAGE_KEY_NAME, stage.toString());
        } else {
            window.sessionStorage.removeItem(SESSION_STORAGE_KEY_NAME);
        }
    }

    private _setAuthorityInSessionStorage(authority: string): void {
        // We need to store the authority and client_id in session storage, because in the silent and popup callback pages
        // we also need to create a UserManager instance. These pages are not served by Angular, so they don't have access
        // to environment variables, and we also want to avoid creating separate html files for different environments.
        // The client_id is required by oidc-client-ts for correct session storage key composition after token exchange.
        window.sessionStorage.setItem(SESSION_STORAGE_AUTHORITY_NAME, authority);
        window.sessionStorage.setItem(SESSION_STORAGE_CLIENT_ID_NAME, this._config.clientId);
    }

    private _getUserManager(authority: string): UserManager {
        if (this._userManager == null) {
            Log.setLogger(this._lgConsole);
            Log.setLevel(Log.INFO);

            const settings = this._getUserManagerSettings(authority);
            this._userManager = new UserManager(settings);

            this._userManager.events.addUserLoaded(user => {
                this._user = user;
            });
        }

        return this._userManager;
    }

    private _getUserManagerSettings(authority: string): UserManagerSettings {
        const currentAppUrl = `${window.location.protocol}//${window.location.host}${this._config.basePath}`;
        const settings = {
            authority,
            extraQueryParams: {
                audience: this._config.audience
            },
            client_id: this._config.clientId,
            redirect_uri: `${currentAppUrl}signin-callback.html`,
            silent_redirect_uri: `${currentAppUrl}silent-callback.html`,
            popup_redirect_uri: `${currentAppUrl}signin-popup-callback.html`,
            response_type: "code",
            scope: this._config.scope ?? "openid",
            automaticSilentRenew: true,
            includeIdTokenInSilentRenew: true
        } as UserManagerSettings;

        /*
         * Auth0 does not return the "end_session_endpoint" so we need to set it ourselves, but
         * oidc-client doesn't let us to specify an override for ONLY that. This means we need to
         * specify ALL of these endpoints. Additionally for the end_session_endpoint there are a
         * couple of possibilities:
         *
         * - Logout from application only
         *       end_session_endpoint: this._config.authority + "/v2/logout"
         *
         * - Federated logout (i.e. also try to log the user out from external IdP)
         *       end_session_endpoint: this._config.authority + "/v2/logout?federated"
         *
         * - Logout to a given URL (i.e. https://app-dev.logex.co.uk/LogexOnline), note that if this is not specified
         *   Auth0 will redirect the user to the first mentioned application in the "Allowed Logout URLs"
         *       end_session_endpoint: this._config.authority + "/v2/logout?returnTo=http%3A%2F%2Fapp-dev.logex.co.uk/LogexOnline"
         *
         * For more info see https://auth0.com/docs/logout/guides/logout-auth0
         */

        if (this._usingAuth0()) {
            settings.metadata = {
                // auth0 issuer always ends with trailing slash
                issuer: `${authority}/`,
                authorization_endpoint: `${authority}/authorize`,
                userinfo_endpoint: `${authority}/userinfo`,
                end_session_endpoint: `${authority}/v2/logout?returnTo=${currentAppUrl}&client_id=${this._config.clientId}`,
                jwks_uri: `${authority}/.well-known/jwks.json`,
                token_endpoint: `${authority}/oauth/token`
            };
        }

        return settings;
    }

    private _usingAuth0(): boolean {
        return this._config.type === "auth0";
    }

    private _getNormalizedAuthority(): string {
        const protocolPrefixRegex = /^https?:\/\//i;
        let authority = this._config.authority.trim();

        // For better compatibility with auth0 js library, support authority value without protocol
        if (!protocolPrefixRegex.test(authority)) {
            authority = `https://${authority}`;
        }
        // Make sure the authority doesn't contain the trailing slash
        if (authority.slice(-1) === "/") {
            authority = authority.slice(0, -1);
        }

        return authority;
    }

    private _onSilentRenewError(err: unknown): void {
        // Silent: don't trigger reload/dialog — the renew retry mechanism handles recovery
        const errorGuid = this._trackNetworkError(err, { silent: true });
        this._lgConsole.error(`Silent renew failed. Error GUID: ${errorGuid ?? "N/A"}`);

        if (!this._user.expired) {
            this._lgConsole.info("Access token is not expired yet, restarting silentRenew");
            this._userManager!.stopSilentRenew();
            setTimeout(() => this._userManager!.startSilentRenew(), SILENT_RENEW_RETRY_DELAY_MS);
        }
    }

    /**
     * Track network errors from OIDC operations (initial login, silent renew, etc.)
     *
     * @returns The generated GUID, or null if tracking fails
     */
    private _trackNetworkError(err: unknown, options?: { silent?: boolean }): string | null {
        const networkErrorService = this._injector.get(LgNetworkErrorService, null);
        if (networkErrorService) {
            const errorMessage = err instanceof Error ? err.message : String(err);
            const guid = networkErrorService.trackError(
                new Error(`OIDC silent renew error: ${errorMessage}`),
                {
                    url: this._config.authority,
                    status: 0
                },
                options
            );
            this._lgConsole.info(`Network error tracked with GUID: ${guid ?? "failed"}`);
            return guid;
        } else {
            this._lgConsole.warn("LgNetworkErrorService not available - error tracking skipped");
            return null;
        }
    }

    private async _onAccessTokenExpired(): Promise<void> {
        this._lgConsole.debug("Access token is expired");

        // We have to obtain the service via injector, because injecting it in the constructor
        // creates circular dependency in runtime
        const promptDialog = this._injector.get(LgPromptDialog);

        // Show alert
        const closeInfoSubject = new Subject<void>();
        promptDialog.infoLc("FW._AuthTokenRenew.AlertTitle", "FW._AuthTokenRenew.TokenExpired", {
            forceClick$: closeInfoSubject.asObservable()
        });

        // Try to renew the token
        let isLoginRequired = false;
        let delayIdx = 0;
        while (true) {
            try {
                await this._userManager!.signinSilent();
                this._lgConsole.info("Access token renewed successfully");
                break;
            } catch (e: any) {
                if (e.error === "login_required") {
                    isLoginRequired = true;
                    break;
                }

                // Silent: intermediate retry — don't trigger reload/dialog
                const errorGuid = this._trackNetworkError(e, { silent: true });
                this._lgConsole.warn(
                    `Token renewal attempt ${delayIdx + 1}/${DELAYS_IN_SECONDS.length} failed. ` +
                        `Error GUID: ${errorGuid ?? "N/A"}. ` +
                        `Retrying in ${DELAYS_IN_SECONDS[delayIdx]}s...`
                );

                // Wait some time before trying to sign in again
                await new Promise<void>(resolve => {
                    setTimeout(() => resolve(), DELAYS_IN_SECONDS[delayIdx] * 1000);
                    if (delayIdx < DELAYS_IN_SECONDS.length - 1) delayIdx++;
                });
            }
        }

        closeInfoSubject.next();
        closeInfoSubject.complete();
        await Promise.resolve();

        if (isLoginRequired) {
            this._lgConsole.info("Login required");

            const button = await promptDialog.confirmLc(
                "FW._AuthTokenRenew.AlertTitle",
                "FW._AuthTokenRenew.ReloginRequired",
                {
                    buttons: [
                        {
                            id: "relogin",
                            nameLc: "FW._AuthTokenRenew.Relogin",
                            isConfirmAction: true
                        },
                        {
                            id: "reload",
                            nameLc: "FW._AuthTokenRenew.Reload",
                            isCancelAction: true
                        }
                    ],
                    allowClose: false,
                    noKeyboard: true
                }
            );
            switch (button) {
                case "relogin":
                    // Here we should have blocked the application screen while login is in progress, but
                    // injection of LgLoaderService creates circular dependency during build, so we need to have
                    // some workaround for this. For now just removed this functionality.
                    // const lgLoaderService = this._injector.get<LgLoaderService>(LgLoaderService);
                    try {
                        // lgLoaderService.show("login");
                        await this._userManager!.signinPopup();
                        this._lgConsole.info("Popup signin completed successfully");
                    } catch (e) {
                        const errorGuid = this._trackNetworkError(e);
                        this._lgConsole.error(
                            `Popup signin failed. Error GUID: ${errorGuid ?? "N/A"}`,
                            e
                        );
                        throw e;
                    } finally {
                        // lgLoaderService.hide("login");
                    }
                    break;

                case "reload":
                    await this._userManager!.signinRedirect({ state: this._config.redirectUrl });
                    break;

                default:
                    throw Error("Should not happen");
            }
        }
    }

    logout(): Promise<void> {
        if (!this.isLoggedIn()) return Promise.resolve();

        return this._userManager!.signoutRedirect();
    }

    async isLoggedIn(): Promise<boolean> {
        return this._loginPromise.then(() => true);
    }

    get loggedIn(): boolean {
        return this._user != null;
    }

    get user(): UserIdentity {
        if (this._user == null) {
            throw Error("User is not logged in");
        }

        if (!this._userIdentity) {
            const profile = this._user.profile as Record<string, string | undefined>;
            this._userIdentity = {
                id: profile["https://logex.nl/legacyuserid"] ?? profile["sub"]!,
                login: profile["https://logex.nl/login"] ?? profile["login"]!,
                name: profile["https://logex.nl/login"] ?? profile["login"]!
            };
        }

        return this._userIdentity;
    }

    async getAccessToken(): Promise<string> {
        return this._user.access_token;
    }

    get idToken(): string {
        return this._user.id_token!;
    }
}
